// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
//
// Leaflet map. Dark basemap + real tectonic plate boundaries (Peter Bird PB2002,
// never hand-authored) + magnitude-sized, depth-coloured markers. The map frame
// is isolated (isolation:isolate, z-index:0) so Leaflet panes never paint over
// drawers/modals; overlays live above z-index 1000.
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { openQuakeDrawer } from '../components/detail';
import { infoI } from '../components/overlays';
import { depthKm, esc, mag as fmtMag, relTime } from '../format';
import { depthCategory, depthColor, magClass } from '../model';
import { quakes } from '../state';
import type { Quake } from '../types';
import { DEPTH_LEGEND } from './shared';

let plateGeo: any = null;
let savedView: { lat: number; lng: number; zoom: number } | null = null;
let showPlates = true;

function markerRadius(mag: number): number {
  if (!Number.isFinite(mag)) return 3;
  return Math.max(2.5, 2.5 + Math.max(0, mag) * 1.7);
}

export async function renderMap(root: HTMLElement): Promise<void> {
  const list = quakes();
  root.innerHTML = `
    <div class="between" style="margin-bottom:0.75rem">
      <div>
        <div class="panel-title">Live map ${infoI('epicentre')}</div>
        <div class="panel-sub" style="margin:0">${list.length} events · marker size = magnitude, colour = ${escOrText('depth')} ${infoI('depth')}. Click a marker for detail.</div>
      </div>
    </div>
    <div class="map-frame">
      <div class="map-canvas" id="qmap"></div>
      <div class="map-toggles">
        <button class="pill-btn ${showPlates ? 'active' : ''}" id="toggle-plates">Plate boundaries</button>
      </div>
      <div class="map-legend">
        <div style="font-weight:600;margin-bottom:4px">Depth</div>
        ${DEPTH_LEGEND.map((d) => `<div class="legend-item"><span class="legend-swatch" style="background:${d.color}"></span>${d.label}</div>`).join('')}
        <div style="margin-top:6px;color:var(--text-tertiary)">◯ larger = stronger · ⦿ ring = tsunami zone</div>
      </div>
    </div>`;

  const canvas = root.querySelector('#qmap') as HTMLElement;
  const map = L.map(canvas, { minZoom: 2, maxZoom: 10, worldCopyJump: true, scrollWheelZoom: false, zoomControl: true, attributionControl: true });
  map.attributionControl.setPrefix(false);

  L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
    attribution: 'Tiles © CARTO · © OpenStreetMap contributors · Quakes: USGS · Plates: Bird (2003)',
    subdomains: 'abcd', minZoom: 2, maxZoom: 10,
  }).addTo(map);

  if (savedView) {
    map.setView([savedView.lat, savedView.lng], savedView.zoom);
  } else {
    map.setView([12, 5], 2);
  }
  map.on('moveend', () => {
    const c = map.getCenter();
    savedView = { lat: c.lat, lng: c.lng, zoom: map.getZoom() };
  });

  // Plate boundaries overlay.
  if (!plateGeo) {
    try {
      plateGeo = await fetch('data/plate-boundaries.geojson').then((r) => (r.ok ? r.json() : null));
    } catch { plateGeo = null; }
  }
  let plateLayer: L.GeoJSON | null = null;
  if (plateGeo) {
    plateLayer = L.geoJSON(plateGeo, {
      style: { color: '#f97316', weight: 1, opacity: 0.5, dashArray: '4 3' },
      onEachFeature: (f, lyr) => {
        const name = f?.properties?.Name ?? 'Plate boundary';
        lyr.bindTooltip(`Plate boundary ${esc(String(name))}`, { sticky: true, className: 'quake-tip' });
      },
    });
    if (showPlates) plateLayer.addTo(map);
  }

  // Quake markers (oldest first so newest/biggest sit on top).
  const ordered = [...list].sort((a, b) => a.mag - b.mag);
  const layer = L.layerGroup();
  for (const q of ordered) {
    const m = L.circleMarker([q.lat, q.lon], {
      radius: markerRadius(q.mag),
      color: q.tsunami ? '#7dd3fc' : 'rgba(255,255,255,0.35)',
      weight: q.tsunami ? 2 : 0.6,
      fillColor: depthColor(q.depth),
      fillOpacity: 0.8,
    });
    m.bindTooltip(tip(q), { direction: 'top', className: 'quake-tip', opacity: 0.97 });
    m.on('click', () => openQuakeDrawer(q));
    m.on('mouseover', () => m.setStyle({ weight: 2.4 }));
    m.on('mouseout', () => m.setStyle({ weight: q.tsunami ? 2 : 0.6 }));
    m.addTo(layer);
  }
  layer.addTo(map);

  root.querySelector('#toggle-plates')?.addEventListener('click', (e) => {
    showPlates = !showPlates;
    (e.currentTarget as HTMLElement).classList.toggle('active', showPlates);
    if (plateLayer) { if (showPlates) plateLayer.addTo(map); else map.removeLayer(plateLayer); }
  });

  // Zero-size defence.
  const fix = () => map.invalidateSize();
  const ro = new ResizeObserver(() => { if (canvas.clientHeight > 50) { fix(); } });
  ro.observe(canvas);
  setTimeout(fix, 200);
  setTimeout(fix, 500);
}

function tip(q: Quake): string {
  return `<strong>M ${fmtMag(q.mag)}</strong> ${esc(magClass(q.mag))}<br>${esc(q.place)}<br>${depthKm(q.depth)} deep · ${esc(depthCategory(q.depth))}<br>${relTime(q.time)}`;
}

function escOrText(s: string): string { return s; }
